import React, { FC, RefObject, useRef, useState } from 'react';
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Checkbox } from '../Checkbox';
import { ProductCardDropdown } from './ProductCardAttributes';
import { get, isEmpty, isEqual } from 'lodash';
import { isCardValid, isDefault, isPlanCardLoading } from './card.utils';
import { CardBottom } from './CardBottom';
import { DefaultCardContent } from './DefaultCardContent';
import { CoverMeProductCard } from './CoverMeProductCard';
import { QuoteStoreResponse, TravellerProductDetails } from '@trip-ux/redux-store';
import CoverMeViewDetails from './CoverMeViewDetails';
import { sanitizeDOM } from '@trip-ux/common';
import { SUPPORTED_LOCALES } from '@trip-ux/constants';

export type CardVariant = 'COVER_ME' | 'DEFAULT';
export interface ProductCardGenericLabels {
  mqAlert?: string;
  mqEditLabel?: string;
  mqLabel?: string;
  mqNotEligible?: string;
  viewDetails?: string;
  viewDetailsAriaLabel?: string;
  selectPlanLabel?: string;
  selectedPlanLabel?: string;
  addonPlanLabel: string;
  addedOnPlanLabel: string;
  deductiblePlaceholder?: string;
  deductibleLabel?: string;
  tripDaysLabel?: string;
  tripDaysPlaceholder?: string;
  tripDaysRequired?: string;
  sumInsuredAfterLabel?: string;
  sumInsuredAfterPlaceholder?: string;
  sumInsuredAfterRequired?: string;
  tripCostLabel?: string;
  tripCostRequired?: string;
  coverageAmtRequired?: string;
  tripCostPlaceholder?: string;
  tripCostError?: string;
  coverageAmtLabel?: string;
  stcihAmtLabel?: string;
  coverageAmtPlaceholder?: string;
  modalCloseLabel?: string;
  eligibilityTitle?: string;
  eligibilityAgreement?: string;
  calculatePremium?: string;
  calculatePremiumSpinner?: string;
  reCalculatePremium?: string;
  tripDaysWithTopUpLabel?: string;
  tripDaysMaxTopUpError?: string;
  detailsLabel?: string;
  exclusionLabel?: string;
  viewDetailsFooter?: string;
  viewPolicy?: string;
  topUpEndDate?: string;
  newTabAriaLabel?: string;
}

export interface ProductCardProps {
  code: string;
  name: string;
  summary: string;
  details: string;
  eligibility?: string;
  exclusion?: string;
  amount?: string;
  deductibles?: ProductCardDropdown[];
  tripDays?: ProductCardDropdown[];
  tripCost?: boolean;
  tripCostMaxAmt?: number;
  paxTripCostValue?: string;
  sumInsuredAfterNeeded?: boolean;
  coverageAmounts?: Record<string, ProductCardDropdown[]>;
  coverageAmtKey?: string;
  isSelected?: boolean;
  isMqRequired: boolean;
  isMqTaken?: boolean;
  genericLabels: ProductCardGenericLabels;
  onParamChange: (cardParams: ProductCardAttributesValues) => void;
  onSelect: (paxId: number, code: string, cardParams: Record<string, string>) => void;
  onDeselect: (paxId: number, code: string) => void;
  mqAction: (paxId: number, action: string) => void;
  onParamCalculate?: (params: ProductCardAttributesValues, code: string) => void;
  quote?: QuoteStoreResponse;
  travellersPlan?: TravellerProductDetails;
  paxId: number;
  isAddOn: boolean;
  optBenAmountBreakdown?: [
    {
      referenceNumber: string;
      premium: number;
      premiumTotal: number;
      premiumTotalLabel: string;
    }
  ];
  values?: ProductCardAttributesValues;
  className?: string;
  id: string;
  premium?: number;
  loadingPremium?: boolean;
  pdfLink?: {
    factSheetPdfLink: string;
    factSheetPdfLinkText: string;
    noticeOfRecissionPdfLink: string;
    noticeOfRecissionPdfLinkText: string;
    policyPdfLink: string;
    policyPdfLinkText: string;
    productPdfLink: string;
    productPdfLinkText: string;
    cmProductPdfLink: string;
  };
  province?: string;
  disabled?: boolean;
  isMqEligible?: boolean;
  variant?: CardVariant;
  onChangeThreshold?: (e: string) => void;
  travellerType?: string;
  tripDaysMaxTopUp?: number;
  tripDaysMinTopUp?: number;
  daysPerTrip?: string;
  viewDetailsRef?: RefObject<HTMLAnchorElement>;
  tripStartDate?: string;
  applicationDate?: string;
  dateFormat?: string;
  tooltips?: {
    deductible: string;
    tripCost: string;
    tripDuration: string;
    coverageAmount: string;
  };
  language?: SUPPORTED_LOCALES;
}

export interface ProductCardAttributesValues {
  deductible?: string;
  daysPerTrip?: string;
  sumInsuredAfter?: string;
  tripCost?: string;
  coverageAmount?: string;
}

export const ProductCard: FC<ProductCardProps> = props => {
  const {
    code,
    name,
    details,
    eligibility,
    exclusion,
    deductibles,
    tripDays,
    sumInsuredAfterNeeded,
    tripCost,
    isMqRequired,
    isSelected,
    isMqTaken,
    genericLabels,
    onSelect,
    onDeselect,
    mqAction,
    paxId,
    isAddOn,
    values = {},
    className = '',
    premium,
    id,
    disabled = false,
    isMqEligible,
    quote,
    travellersPlan,
    variant,
    pdfLink,
    language
  } = props;
  const [showDetails, setShowDetails] = useState(false);
  const [showEligibility, setShowEligibility] = useState(false);
  const [agreeEligibility, setAgreeEligibility] = useState(false);
  const viewDetailsButton = useRef(null);

  const [selection, setSelection] = useState<{ paxId: number; code: string; cardParams: Record<string, string> }>({
    paxId: null,
    code: null,
    cardParams: null
  });
  const isValid = isCardValid(values, deductibles, tripDays, sumInsuredAfterNeeded, tripCost, premium, variant);
  const activeClassName = isSelected ? 'active' : '';
  const defaultType = isDefault(variant);
  const hasEligibility = !isEmpty(eligibility);

  const handleOnSelect: typeof onSelect = (paxRef, planCode, cardParams) => {
    //NOSONAR
    if (isAddOn || !hasEligibility) {
      onSelect(paxRef, planCode, cardParams);
      return;
    }
    setAgreeEligibility(false);
    setShowEligibility(true);
    setSelection({ paxId: paxRef, code: planCode, cardParams });
  };

  const handleAgreeEligibility = () => {
    onSelect(get(selection, 'paxId'), get(selection, 'code'), get(selection, 'cardParams'));
    setShowEligibility(false);
  };

  return (
    <div
      className={`product-card d-flex flex-column alt-primary-semi-dark-brd-hover alt-primary-semi-dark-brd-active
      ${className} ${activeClassName}`}>
      {defaultType && <DefaultCardContent {...props} className={activeClassName} setShowDetails={setShowDetails} />}
      {isEqual(variant, 'COVER_ME') && (
        <CoverMeProductCard
          {...props}
          className={activeClassName}
          setShowDetails={setShowDetails}
          viewDetailsRef={viewDetailsButton}
          language={language}
        />
      )}
      <CardBottom
        cardParams={values}
        code={code}
        genericLabels={genericLabels}
        id={id}
        isAddOn={isAddOn}
        isMqRequired={isMqRequired}
        isMqTaken={isMqTaken}
        isSelected={isSelected}
        isValid={isValid && !disabled && !isPlanCardLoading(travellersPlan, quote, paxId, code)}
        mqAction={mqAction}
        name={name}
        onDeselect={onDeselect}
        onSelect={handleOnSelect}
        paxId={paxId}
        isMqEligible={isMqEligible}
        variant={variant}
      />
      <Modal
        onClose={setShowDetails}
        isOpen={showDetails}
        closeOnBackdropClick={false}
        id={`product-${code}-details-${paxId}`}
        closeAriaLabel={get(genericLabels, 'modalCloseLabel')}
        className="modal-dialog modal-lg product-card-modal"
        ariaLabel={name}
        ariaLabelledby={`product-${code}-label`}
        ariaDescribedby={`product-${code}-content`}
        returnFocusComponent={viewDetailsButton}>
        <h2 id={`product-${code}-label`} dangerouslySetInnerHTML={{ __html: sanitizeDOM(name) }} />
        {defaultType ? (
          <div
            id={`product-${code}-content`}
            className="pt-4 text-break"
            dangerouslySetInnerHTML={{ __html: sanitizeDOM(details) }}
            data-testid="product-content-details"
          />
        ) : (
          <CoverMeViewDetails
            code={code}
            details={details}
            exclusion={exclusion}
            genericLabels={genericLabels}
            pdfLink={pdfLink}
          />
        )}
      </Modal>
      {hasEligibility && (
        <Modal
          onClose={setShowEligibility}
          isOpen={showEligibility}
          closeOnBackdropClick={false}
          id={`product-${code}-eligibility-${paxId}`}
          closeAriaLabel={get(genericLabels, 'modalCloseLabel')}
          className="modal-dialog modal-lg"
          ariaLabel={get(genericLabels, 'eligibilityTitle')}
          ariaLabelledby={`product-${code}-eligibility-label`}
          ariaDescribedby={`product-${code}-eligibility-content`}>
          <h2
            id={`product-${code}-eligibility-label`}
            dangerouslySetInnerHTML={{ __html: sanitizeDOM(get(genericLabels, 'eligibilityTitle')) }}
          />
          <div
            id={`product-${code}-eligibility-content`}
            className="pt-4 text-break"
            dangerouslySetInnerHTML={{ __html: sanitizeDOM(eligibility) }}
            data-testid="product-content-eligibility"
          />
          <Checkbox
            name={`product-${code}-agreeEligibility`}
            className="my-4 d-flex"
            checked={agreeEligibility}
            onChange={() => setAgreeEligibility(!agreeEligibility)}>
            {get(genericLabels, 'eligibilityAgreement')}
          </Checkbox>
          <Button
            ariaLabel={`${get(genericLabels, 'selectPlanLabel')} ${name}`}
            disabled={!agreeEligibility}
            type="button"
            className="mx-auto"
            onClick={handleAgreeEligibility}
            id={`product-${code}-agreeEligibility-button`}>
            {get(genericLabels, 'selectPlanLabel')}
          </Button>
        </Modal>
      )}
    </div>
  );
};

ProductCard.displayName = 'ProductCard';
export default ProductCard;
